import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { PermissionEnum, ResponseError, responseErrorsResolver } from '@ui/src';
import {
  useLazyGetCorporateCOFQuery,
  useUpdateCorporateInfoMutation,
} from '../../store/services/corporate';
import {
  useAddCommentToCustomerMutation,
  useDeleteCommentInCustomerMutation,
  useEditCommentInCustomerMutation,
  useLazyGetCustomerCommentsQuery,
  useLazyGetCustomersChangeLogsQuery,
  useReopenOrganizationMutation,
} from '../../../customers/store/services/customers';
import { CorporateOverviewGeneral } from '../../types';
import { CustomerProps } from '../../../common/types';

const useOverview = ({
  customerId,
  activePermissions,
  baseInfo,
}: {
  customerId?: string;
  activePermissions?: PermissionEnum[];
  baseInfo?: Partial<CorporateOverviewGeneral>;
}) => {
  const [topUpPanelIsOpen, setTopUpPanelIsOpen] = useState(false);
  const [selectedTopUpId, setSelectedTopUpId] = useState('');
  const [globalError, setGlobalError] = useState('');
  const [changeLogsPanelIsOpen, setChangeLogPanelIsOpen] = useState(false);
  const [reopenPopupVisible, setReopenPopupVisible] = useState(false);
  const [reopenPopupState, setReopenPopupState] = useState<
    'warning' | 'comment'
  >('warning');
  const [reopenReason, setReopenReason] = useState('');
  const [reopenError, setReopenError] = useState('');

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
    setError,
  } = useForm<CorporateOverviewGeneral>();

  const correspondenceAddressSameAsRegisteredAddress = watch(
    'correspondenceAddressSameAsRegisteredAddress',
  );
  const businessAddressSameAsRegisteredAddress = watch(
    'businessAddressSameAsRegisteredAddress',
  );

  const [getCorporateCof, { isLoading: cofIsLoading }] =
    useLazyGetCorporateCOFQuery();

  const [
    getChangeLogs,
    { isLoading: changeLogsIsLoading, isFetching: changeLogsIsFetching },
  ] = useLazyGetCustomersChangeLogsQuery();

  const [
    reopenOrganization,
    { isLoading: reopenInProgress, isSuccess: reopenIsSuccess },
  ] = useReopenOrganizationMutation();

  const [
    getCustomerComments,
    { isLoading: commentsIsLoading, isFetching: commentsIsFetching },
  ] = useLazyGetCustomerCommentsQuery();
  const [
    addCommentToCustomer,
    { isLoading: addCommentInProgress, isSuccess: addCommentIsSuccess },
  ] = useAddCommentToCustomerMutation();
  const [
    editCommentInCustomer,
    { isLoading: editCommentInProgress, isSuccess: editCommentIsSuccess },
  ] = useEditCommentInCustomerMutation();
  const [
    deleteCommentInCustomer,
    { isLoading: deleteCommentInProgress, isSuccess: deleteCommentIsSuccess },
  ] = useDeleteCommentInCustomerMutation();

  const [updateCorporateInfo, { isLoading, isSuccess }] =
    useUpdateCorporateInfoMutation();

  const onReopenOrganization = async () => {
    setReopenError('');

    const response = (await reopenOrganization({
      id: customerId as string,
      body: {
        statusChangeReason: reopenReason,
      },
    })) as { error: ResponseError };

    if (response.error) {
      responseErrorsResolver({
        errors: response.error,
        setError: () => {},
        setGlobalError: setReopenError,
      });
    }
  };

  const getChangeLogsAction = useCallback(
    (cursor?: string) => {
      getChangeLogs(
        `?take=20&entityType=ORGANIZATION&entityId=${customerId}${
          cursor ? `&cursor=${cursor}` : ''
        }`,
      );
    },
    [customerId],
  );

  const onSubmit = handleSubmit(async (values) => {
    setGlobalError('');

    const body: Partial<CorporateOverviewGeneral> = {
      registeredCountryId: values.registeredCountryId,
      registeredCity: values.registeredCity,
      registeredPostalCode: values.registeredPostalCode,
      registeredAddress1: values.registeredAddress1,
      registeredAddress2: values.registeredAddress2,
      companyNumber: values.companyNumber,
      dateOfIncorporation: values.dateOfIncorporation,
      countryOfIncorporationId: values.countryOfIncorporationId,
      websiteLink: values.websiteLink,
      sicCodes: values.sicCodes,
      companyName: values.companyName,
      productIds: values.productIds,
      merchantId: values?.merchantId || '',
      businessAddressSameAsRegisteredAddress:
        values.businessAddressSameAsRegisteredAddress,
      correspondenceAddressSameAsRegisteredAddress:
        values.correspondenceAddressSameAsRegisteredAddress,
    };

    if (values.businessDescription) {
      body.businessDescription = values.businessDescription;
    }

    if (values.vatNumber) {
      body.vatNumber = values.vatNumber;
    }

    if (!correspondenceAddressSameAsRegisteredAddress) {
      if (values.correspondenceCountryId) {
        body.correspondenceCountryId = values.correspondenceCountryId;
      }

      if (values.correspondencePostalCode) {
        body.correspondencePostalCode = values.correspondencePostalCode;
      }

      if (values.correspondenceCity) {
        body.correspondenceCity = values.correspondenceCity;
      }

      if (values.correspondenceAddress1) {
        body.correspondenceAddress1 = values.correspondenceAddress1;
      }

      if (values.correspondenceAddress2) {
        body.correspondenceAddress2 = values.correspondenceAddress2;
      }
    } else {
      if (baseInfo?.correspondenceCountryId) {
        body.correspondenceCountryId = null;
      }

      if (baseInfo?.correspondencePostalCode) {
        body.correspondencePostalCode = null;
      }

      if (baseInfo?.correspondenceCity) {
        body.correspondenceCity = null;
      }

      if (baseInfo?.correspondenceAddress1) {
        body.correspondenceAddress1 = null;
      }

      if (baseInfo?.correspondenceAddress2) {
        body.correspondenceAddress2 = null;
      }
    }

    if (!businessAddressSameAsRegisteredAddress) {
      if (values.businessCountryId) {
        body.businessCountryId = values.businessCountryId;
      }

      if (values.businessCity) {
        body.businessCity = values.businessCity;
      }

      if (values.businessPostalCode) {
        body.businessPostalCode = values.businessPostalCode;
      }

      if (values.businessAddress1) {
        body.businessAddress1 = values.businessAddress1;
      }

      if (values.businessAddress2) {
        body.businessAddress2 = values.businessAddress2;
      }
    } else {
      if (baseInfo?.businessCountryId) {
        body.businessCountryId = null;
      }

      if (baseInfo?.businessCity) {
        body.businessCity = null;
      }

      if (baseInfo?.businessPostalCode) {
        body.businessPostalCode = null;
      }

      if (baseInfo?.businessAddress1) {
        body.businessAddress1 = null;
      }

      if (baseInfo?.businessAddress2) {
        body.businessAddress2 = null;
      }
    }

    const response = (await updateCorporateInfo({
      id: customerId as string,
      body,
    })) as {
      data?: { data: CustomerProps };
      error?: ResponseError;
    };

    if (response.error) {
      responseErrorsResolver({
        errors: response.error,
        setError,
        setGlobalError,
      });
    }
  });

  const onAddCommentToCustomer = async (text: string) => {
    await addCommentToCustomer({
      id: customerId as string,
      body: {
        text,
      },
    });
  };

  const onEditCommentInCustomer = async ({
    text,
    commentId,
  }: {
    text: string;
    commentId: string;
  }) => {
    await editCommentInCustomer({
      id: customerId as string,
      commentId,
      body: {
        text,
      },
    });
  };

  const onDeleteCommentInCustomer = async (commentId: string) => {
    await deleteCommentInCustomer({
      id: customerId as string,
      commentId,
    });
  };

  const getCustomerCommentsAction = async (cursor?: string) => {
    await getCustomerComments({
      id: customerId as string,
      cursor: cursor || '',
    });
  };

  useEffect(() => {
    if (
      activePermissions?.includes(
        PermissionEnum.CORPORATE_CUSTOMERS_COMMENTS_LIST,
      )
    ) {
      getCustomerCommentsAction();
    }
  }, []);

  useEffect(() => {
    if (customerId) {
      getCorporateCof(customerId);
    }
  }, [customerId]);

  useEffect(() => {
    if (
      changeLogsPanelIsOpen &&
      activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST)
    ) {
      getChangeLogsAction();
    }
  }, [changeLogsPanelIsOpen]);

  useEffect(() => {
    if (baseInfo) {
      reset(baseInfo);
    }
  }, [baseInfo]);

  useEffect(() => {
    if (reopenIsSuccess) {
      setReopenError('');
      setReopenPopupState('warning');
      setReopenPopupVisible(false);
    }
  }, [reopenIsSuccess]);

  return {
    setChangeLogPanelIsOpen,
    setReopenPopupVisible,
    control,
    correspondenceAddressSameAsRegisteredAddress,
    businessAddressSameAsRegisteredAddress,
    onSubmit,
    isLoading,
    isSuccess,
    errors,
    globalError,
    commentsIsLoading,
    commentsIsFetching,
    getCustomerCommentsAction,
    onAddCommentToCustomer,
    addCommentInProgress,
    addCommentIsSuccess,
    onEditCommentInCustomer,
    editCommentInProgress,
    editCommentIsSuccess,
    onDeleteCommentInCustomer,
    deleteCommentInProgress,
    deleteCommentIsSuccess,
    cofIsLoading,
    setTopUpPanelIsOpen,
    setSelectedTopUpId,
    topUpPanelIsOpen,
    selectedTopUpId,
    reopenPopupState,
    setReopenPopupState,
    setReopenError,
    reopenInProgress,
    reopenPopupVisible,
    reopenReason,
    onReopenOrganization,
    reopenError,
    setReopenReason,
    changeLogsPanelIsOpen,
    changeLogsIsLoading,
    changeLogsIsFetching,
    getChangeLogsAction,
  };
};

export default useOverview;
